import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Edit, FileText, UserPlus, UserX } from 'react-feather';
import DataTable from '../../../Common/DataTable';
import { Btn } from '../../../../AbstractElements';
import { fetchTableData, patchFinalCompensationInfo, assignFinalCompensationToSelf, releaseFinalCompensationFromSelf } from '../../../../Api/ApiMethods';
import { normalizeFieldValue, parseCurrencyToFloat, displayDecimal } from '../../../../_helper/regexHandler';

const COVER_INFO_PENDING_LABEL = 'kapak bilgileri bekleniyor';

const createEmptyCompensationInfoForm = () => ({
    acceptedAmount: '',
    enforcementAttorneyFee: '',
    judgmentAttorneyFee: '',
    interestAmount: '',
    additionalExpenseAmount: '',
    comments: [{ commentType: '', comment: '' }]
});

const formatDate = (value) => {
    if (!value) {
        return '-';
    }
    const dateInstance = new Date(value);
    return Number.isNaN(dateInstance.getTime()) ? '-' : dateInstance.toLocaleDateString('tr-TR');
};

const formatCurrency = (value) => {
    if (value === null || value === undefined) {
        return '-';
    }
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
        return '-';
    }
    return `${numericValue.toLocaleString('tr-TR')} ₺`;
};

const extractPrimaryPaymentCollection = (paymentCollections) => {
    if (!paymentCollections) {
        return null;
    }
    if (Array.isArray(paymentCollections)) {
        return paymentCollections.length ? paymentCollections[0] : null;
    }
    if (typeof paymentCollections === 'object') {
        return paymentCollections;
    }
    return null;
};

const extractCompensationInfoFromRow = (row) => {
    const candidates = [
        row?.compensationInfo,
        row?.compensationInformation,
        row?.finalCompensationInfo,
        row?.finalCompensationInformation,
        row?.compensationDetails,
        row?.compensationDto,
        row?.compensation
    ];
    return candidates.find(info => info && typeof info === 'object') || null;
};

const getFinalCompensationStatusLabel = (row) => {
    if (!row) {
        return '';
    }

    const candidates = [
        row.status,
        row.status?.title,
        row.state,
        row.state?.title,
        row.compensationState,
        row.compensationState?.title,
        row.compensationState?.status,
        row.compensationStateTitle,
        row.compensationStatus,
        row.finalCompensationStatus,
        row.finalCompensationState?.title,
        row.finalCompensationState?.status,
        row.statusTitle,
        row.currentStatus,
        row.compensationInformation?.status,
        row.compensationInformation?.statusTitle,
        row.compensationInformation?.state?.title,
        row.compensationInformation?.stateTitle,
        row.compensationInfo?.status,
        row.compensationInfo?.statusTitle,
        row.compensationInfo?.state?.title,
        row.compensationInfo?.stateTitle
    ];

    const resolved = candidates.find((value) => {
        if (!value) {
            return false;
        }
        if (typeof value === 'string') {
            return value.trim().length > 0;
        }
        if (typeof value === 'object' && typeof value.title === 'string') {
            return value.title.trim().length > 0;
        }
        return false;
    });

    if (!resolved) {
        return '';
    }

    if (typeof resolved === 'string') {
        return resolved;
    }

    if (typeof resolved === 'object' && typeof resolved.title === 'string') {
        return resolved.title;
    }

    return '';
};

const isCoverInformationPending = (row) => {
    const statusLabel = getFinalCompensationStatusLabel(row);
    return typeof statusLabel === 'string' && statusLabel.trim().toLowerCase() === COVER_INFO_PENDING_LABEL;
};

const TodoTabFinalCompensation = ({ onCountChange, onLoadingChange }) => {
    const navigate = useNavigate();
    const isInitialLoad = useRef(true);
    const searchRef = useRef({
        customerSearch: '',
        licensePlateSearch: '',
        accidentDateSearch: '',
        enforcementCaseSearch: '',
        arbitrationCaseSearch: '',
        paymentDateSearch: ''
    });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [customerSearch, setCustomerSearch] = useState('');
    const [licensePlateSearch, setLicensePlateSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');
    const [enforcementCaseSearch, setEnforcementCaseSearch] = useState('');
    const [arbitrationCaseSearch, setArbitrationCaseSearch] = useState('');
    const [paymentDateSearch, setPaymentDateSearch] = useState('');

    const [isCompensationInfoModalOpen, setIsCompensationInfoModalOpen] = useState(false);
    const [currentCompensationItem, setCurrentCompensationItem] = useState(null);
    const [compensationInfoForm, setCompensationInfoForm] = useState(createEmptyCompensationInfoForm());
    const [isSubmittingCompensationInfo, setIsSubmittingCompensationInfo] = useState(false);
    const [isAssigningCompensation, setIsAssigningCompensation] = useState(false);
    const [isReleasingCompensation, setIsReleasingCompensation] = useState(false);

    useEffect(() => {
        if (typeof onCountChange === 'function') {
            onCountChange(totalRows);
        }
    }, [onCountChange, totalRows]);

    useEffect(() => {
        if (typeof onLoadingChange === 'function') {
            onLoadingChange(loading);
        }
    }, [loading, onLoadingChange]);

    useEffect(() => {
        searchRef.current = {
            customerSearch,
            licensePlateSearch,
            accidentDateSearch,
            enforcementCaseSearch,
            arbitrationCaseSearch,
            paymentDateSearch
        };
    }, [
        customerSearch,
        licensePlateSearch,
        accidentDateSearch,
        enforcementCaseSearch,
        arbitrationCaseSearch,
        paymentDateSearch
    ]);

    const fetchFinalCompensationData = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/FinalCompensation';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();

            if (customerSearch && customerSearch.trim().length >= 2) {
                queryParams.append('customer', customerSearch.trim());
            }
            if (licensePlateSearch && licensePlateSearch.trim().length >= 2) {
                queryParams.append('licensePlate', licensePlateSearch.trim());
            }
            if (accidentDateSearch) {
                queryParams.append('accidentDate', accidentDateSearch);
            }
            if (enforcementCaseSearch && enforcementCaseSearch.trim().length >= 3) {
                queryParams.append('enforcementCaseNumber', enforcementCaseSearch.trim());
            }
            if (arbitrationCaseSearch && arbitrationCaseSearch.trim().length >= 3) {
                queryParams.append('arbitrationCaseNumber', arbitrationCaseSearch.trim());
            }
            if (paymentDateSearch) {
                queryParams.append('paymentDate', paymentDateSearch);
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: (rows) => {
                    const filtered = (Array.isArray(rows) ? rows : []).filter(r => r.compensationState?.order !== 3);
                    setItems(filtered);
                    setTotalRows(filtered.length);
                },
                setTotalRowsCallback: () => {}
            });
        } catch (error) {
            toast.error('Kapak bilgileri alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [
        page,
        pageSize,
        customerSearch,
        licensePlateSearch,
        accidentDateSearch,
        enforcementCaseSearch,
        arbitrationCaseSearch,
        paymentDateSearch
    ]);

    const fetchFinalCompensationDataNonSearch = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/FinalCompensation';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();

            const {
                customerSearch: currentCustomerSearch,
                licensePlateSearch: currentLicenseSearch,
                accidentDateSearch: currentAccidentDateSearch,
                enforcementCaseSearch: currentEnforcementCaseSearch,
                arbitrationCaseSearch: currentArbitrationCaseSearch,
                paymentDateSearch: currentPaymentDateSearch
            } = searchRef.current;

            if (currentCustomerSearch && currentCustomerSearch.trim().length >= 2) {
                queryParams.append('customer', currentCustomerSearch.trim());
            }
            if (currentLicenseSearch && currentLicenseSearch.trim().length >= 2) {
                queryParams.append('licensePlate', currentLicenseSearch.trim());
            }
            if (currentAccidentDateSearch) {
                queryParams.append('accidentDate', currentAccidentDateSearch);
            }
            if (currentEnforcementCaseSearch && currentEnforcementCaseSearch.trim().length >= 3) {
                queryParams.append('enforcementCaseNumber', currentEnforcementCaseSearch.trim());
            }
            if (currentArbitrationCaseSearch && currentArbitrationCaseSearch.trim().length >= 3) {
                queryParams.append('arbitrationCaseNumber', currentArbitrationCaseSearch.trim());
            }
            if (currentPaymentDateSearch) {
                queryParams.append('paymentDate', currentPaymentDateSearch);
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            await fetchTableData({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: (rows) => {
                    const filtered = (Array.isArray(rows) ? rows : []).filter(r => r.compensationState?.order !== 3);
                    setItems(filtered);
                    setTotalRows(filtered.length);
                },
                setTotalRowsCallback: () => {}
            });
        } catch (error) {
            toast.error('Kapak bilgileri alınırken bir hata oluştu.');
            setItems([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchFinalCompensationDataNonSearch();
    }, [fetchFinalCompensationDataNonSearch, page, pageSize]);

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        const hasAnySearch = customerSearch.trim()
            || licensePlateSearch.trim()
            || accidentDateSearch.trim()
            || enforcementCaseSearch.trim()
            || arbitrationCaseSearch.trim()
            || paymentDateSearch.trim();

        const timeoutId = setTimeout(() => {
            const trimmedCustomer = customerSearch.trim();
            const trimmedLicense = licensePlateSearch.trim();
            const trimmedEnforcementCase = enforcementCaseSearch.trim();
            const trimmedArbitrationCase = arbitrationCaseSearch.trim();

            const hasCustomerSearch = trimmedCustomer.length > 0;
            const hasValidCustomerSearch = trimmedCustomer.length >= 2;
            const hasLicenseSearch = trimmedLicense.length > 0;
            const hasValidLicenseSearch = trimmedLicense.length >= 2;
            const hasEnforcementCaseSearch = trimmedEnforcementCase.length > 0;
            const hasValidEnforcementCaseSearch = trimmedEnforcementCase.length >= 3;
            const hasArbitrationCaseSearch = trimmedArbitrationCase.length > 0;
            const hasValidArbitrationCaseSearch = trimmedArbitrationCase.length >= 3;

            if (hasAnySearch) {
                if ((hasCustomerSearch && !hasValidCustomerSearch)
                    || (hasLicenseSearch && !hasValidLicenseSearch)
                    || (hasEnforcementCaseSearch && !hasValidEnforcementCaseSearch)
                    || (hasArbitrationCaseSearch && !hasValidArbitrationCaseSearch)) {
                    return;
                }

                fetchFinalCompensationData();
            } else {
                fetchFinalCompensationDataNonSearch();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [
        customerSearch,
        licensePlateSearch,
        accidentDateSearch,
        enforcementCaseSearch,
        arbitrationCaseSearch,
        paymentDateSearch,
        fetchFinalCompensationData,
        fetchFinalCompensationDataNonSearch
    ]);

    const handleCustomerSearchChange = (event) => {
        setCustomerSearch(event.target.value);
        setPage(1);
    };

    const handleLicensePlateSearchChange = (event) => {
        const rawValue = event.target.value;
        const normalizedValue = normalizeFieldValue('plateSearch', rawValue);
        setLicensePlateSearch(normalizedValue);
        setPage(1);
    };

    const handleAccidentDateSearchChange = (event) => {
        setAccidentDateSearch(event.target.value);
        setPage(1);
    };

    const handleEnforcementCaseSearchChange = (event) => {
        const rawValue = event.target.value;
        const normalizedValue = normalizeFieldValue('enforcementCaseSearch', rawValue);
        setEnforcementCaseSearch(normalizedValue);
        setPage(1);
    };

    const handleArbitrationCaseSearchChange = (event) => {
        const rawValue = event.target.value;
        const normalizedValue = normalizeFieldValue('arbitrationCaseSearch', rawValue);
        setArbitrationCaseSearch(normalizedValue);
        setPage(1);
    };

    const handlePaymentDateSearchChange = (event) => {
        setPaymentDateSearch(event.target.value);
        setPage(1);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (newPerPage, newPage) => {
        setPageSize(newPerPage);
        setPage(newPage);
    };

    const handleAssignToSelf = async (compensationId) => {
        if (isAssigningCompensation) return;
        setIsAssigningCompensation(true);
        try {
            const responseData = await assignFinalCompensationToSelf({ finalCompensationId: compensationId });
            toast.success(responseData?.message || 'Kapak işi başarıyla kendinize atandı!');
            fetchFinalCompensationDataNonSearch();
        } catch {
            // Hata API fonksiyonunda handle ediliyor
        } finally {
            setIsAssigningCompensation(false);
        }
    };

    const handleReleaseFromSelf = async (compensationId) => {
        if (isReleasingCompensation) return;
        setIsReleasingCompensation(true);
        try {
            const responseData = await releaseFinalCompensationFromSelf({ finalCompensationId: compensationId });
            toast.success(responseData?.message || 'Kapak görevi başarıyla kaldırıldı!');
            fetchFinalCompensationDataNonSearch();
        } catch {
            // Hata API fonksiyonunda handle ediliyor
        } finally {
            setIsReleasingCompensation(false);
        }
    };

    const openCompensationInfoModal = (item) => {
        if (!item) {
            return;
        }

        const existingInfo = extractCompensationInfoFromRow(item);
        const existingComments = Array.isArray(existingInfo?.comments) ? existingInfo.comments : [];

        const getComment = (type) => {
            const found = existingComments.find(c => c.commentType === type);
            if (!found && existingComments.length > 0) {
                return { ...existingComments[0], commentType: type };
            }
            return found ? { ...found } : { commentType: type, comment: '' };
        };

        setCurrentCompensationItem(item);
        setCompensationInfoForm({
            acceptedAmount: existingInfo?.acceptedAmount ?? '',
            enforcementAttorneyFee: existingInfo?.enforcementAttorneyFee ?? '',
            judgmentAttorneyFee: existingInfo?.judgmentAttorneyFee ?? '',
            interestAmount: existingInfo?.interestAmount ?? '',
            additionalExpenseAmount: existingInfo?.additionalExpenseAmount ?? '',
            comments: [getComment('Açıklama')]
        });
        setIsCompensationInfoModalOpen(true);
    };

    const closeCompensationInfoModal = () => {
        setIsCompensationInfoModalOpen(false);
        setCurrentCompensationItem(null);
        setCompensationInfoForm(createEmptyCompensationInfoForm());
    };

    const handleCompensationInfoFieldChange = (field, value) => {
        const processedValue = normalizeFieldValue(field, value);
        setCompensationInfoForm((prev) => ({
            ...prev,
            [field]: processedValue
        }));
    };

    const handleCompensationCommentChange = (index, field, value) => {
        setCompensationInfoForm((prev) => ({
            ...prev,
            comments: prev.comments.map((comment, idx) => (
                idx === index ? { ...comment, [field]: value } : comment
            ))
        }));
    };

    const handleSubmitCompensationInfo = async () => {
        if (!currentCompensationItem?.id) {
            toast.error('Güncellenecek kayıt bulunamadı.');
            return;
        }

        const payload = {
            acceptedAmount: parseCurrencyToFloat(compensationInfoForm.acceptedAmount),
            enforcementAttorneyFee: parseCurrencyToFloat(compensationInfoForm.enforcementAttorneyFee),
            judgmentAttorneyFee: parseCurrencyToFloat(compensationInfoForm.judgmentAttorneyFee),
            interestAmount: parseCurrencyToFloat(compensationInfoForm.interestAmount),
            additionalExpenseAmount: parseCurrencyToFloat(compensationInfoForm.additionalExpenseAmount),
            comments: compensationInfoForm.comments
                .map((comment) => ({
                    commentType: (comment.commentType || '').trim(),
                    comment: (comment.comment || '').trim()
                }))
                .filter((comment) => comment.comment)
        };

        setIsSubmittingCompensationInfo(true);

        try {
            await patchFinalCompensationInfo({
                id: currentCompensationItem.id,
                payload
            });

            toast.success('Kapak bilgileri başarıyla güncellendi.');
            closeCompensationInfoModal();
            await fetchFinalCompensationData();
        } catch (error) {
            toast.error(error.message || 'Kapak bilgileri güncellenirken bir hata oluştu.');
        } finally {
            setIsSubmittingCompensationInfo(false);
        }
    };

    const columns = useMemo(() => ([
        {
            name: 'Müşteri',
            selector: (row) => {
                if (row.customerName) {
                    return row.customerName;
                }
                const customer = row.customer;
                if (!customer) {
                    return '-';
                }
                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || customer.name || '-';
                }
                return customer.clientName || customer.fullName || customer.name || '-';
            },
            sortable: true,
            grow: 2,
            minWidth: '200px'
        },
        {
            name: 'Plaka',
            selector: (row) => {
                const licensePlate = row.licensePlate?.licensePlateNumber
                    || row.licensePlateNumber
                    || row.licensePlate
                    || row.accident?.licensePlate?.licensePlateNumber;
                return licensePlate || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Tarihi',
            selector: (row) => {
                const accidentDate = row.accidentDate || row.accident?.accidentDate;
                return formatDate(accidentDate);
            },
            sortable: true,
            grow: 1,
            minWidth: '130px'
        },
        {
            name: 'İcra Dosya No',
            selector: (row) => {
                const enforcementCase = row.enforcementCaseNumber
                    || row.enforcementCaseNo
                    || row.enforcement?.caseNumber;
                return enforcementCase || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '150px'
        },
        {
            name: 'Tahkim Dosya No',
            selector: (row) => {
                const arbitrationCase = row.fileCaseNumber
                    || row.arbitrationCaseNumber
                    || row.arbitration?.caseNumber;
                return arbitrationCase || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '150px'
        },
        {
            name: 'Tahsilat Tutarı',
            selector: (row) => {
                const paymentCollection = extractPrimaryPaymentCollection(row.paymentCollections);
                const collectedAmount = paymentCollection?.amount
                    ?? paymentCollection?.paymentAmount
                    ?? row.collectedAmount;
                return formatCurrency(collectedAmount);
            },
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Müşteriye Ödenecek',
            selector: (row) => {
                const paymentCollection = extractPrimaryPaymentCollection(row.paymentCollections);
                const amountToCustomer = paymentCollection?.amountToBePayedToCustomer
                    ?? row.amountToBePayedToCustomer;
                return formatCurrency(amountToCustomer);
            },
            sortable: true,
            grow: 1,
            minWidth: '160px'
        },
        {
            name: 'Ödeme Tarihi',
            selector: (row) => {
                const paymentCollection = extractPrimaryPaymentCollection(row.paymentCollections);
                const paymentDate = row.paymentDate || paymentCollection?.paymentDate;
                return formatDate(paymentDate);
            },
            sortable: true,
            grow: 1,
            minWidth: '130px'
        },
        {
            name: 'Ödeme Durumu',
            selector: (row) => {
                const paymentCollection = extractPrimaryPaymentCollection(row.paymentCollections);
                return paymentCollection?.status || paymentCollection?.paymentStatus || row.paymentStatus || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Tahsilat No',
            selector: (row) => {
                const paymentCollection = extractPrimaryPaymentCollection(row.paymentCollections);
                return paymentCollection?.paymentCollectionNumber || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '150px'
        },
        {
            name: 'Ata / Kaldır',
            cell: (row) => {
                const appointedUser = row.appointedUser || row.appointedUsers;
                const hasAppointedUser = appointedUser && (
                    Array.isArray(appointedUser)
                        ? appointedUser.filter(u => !!u && String(u).trim() !== '').length > 0
                        : !!appointedUser
                );

                if (!hasAppointedUser) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-primary',
                                size: 'sm',
                                onClick: () => handleAssignToSelf(row.id),
                                title: 'Kendine Ata',
                                disabled: isAssigningCompensation
                            }}
                        >
                            <UserPlus size={16} />
                        </Btn>
                    );
                }

                return (
                    <Btn
                        attrBtn={{
                            color: 'outline-danger',
                            size: 'sm',
                            onClick: () => handleReleaseFromSelf(row.id),
                            title: 'Kendinden Kaldır',
                            disabled: isReleasingCompensation
                        }}
                    >
                        <UserX size={16} />
                    </Btn>
                );
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '110px',
            center: true
        },
        {
            name: 'İşlemler',
            cell: (row) => {
                if (row.compensationState?.title === 'Kapak İşlemleri Bekleniyor') {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-primary',
                                size: 'sm',
                                onClick: () => navigate(`${process.env.PUBLIC_URL}/compensation-detail/${row.id}`),
                                title: 'Kapak detaylarını düzenle'
                            }}
                        >
                            <Edit size={16} />
                        </Btn>
                    );
                }

                if (isCoverInformationPending(row)) {
                    return (
                        <Btn
                            attrBtn={{
                                color: 'outline-primary',
                                size: 'sm',
                                onClick: () => openCompensationInfoModal(row),
                                title: 'Kapak bilgilerini güncelle'
                            }}
                        >
                            <FileText size={16} />
                        </Btn>
                    );
                }
                return '-';
            },
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '130px',
            center: true
        }
    ]), [navigate, openCompensationInfoModal]);

    return (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row className="mb-3">
                        <Col md="4">
                            <FormGroup>
                                <Label>Müşteri Adı ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="Müşteri adı... (en az 2 karakter)"
                                    value={customerSearch}
                                    onChange={handleCustomerSearchChange}
                                />
                                {customerSearch && customerSearch.trim().length > 0 && customerSearch.trim().length < 2 && (
                                    <small className="text-muted">En az 2 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Plaka ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="Plaka... (en az 2 karakter)"
                                    value={licensePlateSearch}
                                    onChange={handleLicensePlateSearchChange}
                                />
                                {licensePlateSearch && licensePlateSearch.trim().length > 0 && licensePlateSearch.trim().length < 2 && (
                                    <small className="text-muted">En az 2 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Kaza Tarihi</Label>
                                <Input type="date" value={accidentDateSearch} onChange={handleAccidentDateSearchChange} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md="4">
                            <FormGroup>
                                <Label>İcra Dosya No ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="İcra dosya numarası... (en az 3 karakter)"
                                    value={enforcementCaseSearch}
                                    onChange={handleEnforcementCaseSearchChange}
                                />
                                {enforcementCaseSearch && enforcementCaseSearch.trim().length > 0 && enforcementCaseSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Tahkim Dosya No ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="Tahkim dosya numarası... (en az 3 karakter)"
                                    value={arbitrationCaseSearch}
                                    onChange={handleArbitrationCaseSearchChange}
                                />
                                {arbitrationCaseSearch && arbitrationCaseSearch.trim().length > 0 && arbitrationCaseSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Ödeme Tarihi</Label>
                                <Input type="date" value={paymentDateSearch} onChange={handlePaymentDateSearchChange} />
                            </FormGroup>
                        </Col>
                    </Row>
                    <DataTable
                        columns={columns}
                        data={items}
                        progressPending={loading}
                        pagination
                        paginationServer
                        stickyActionPosition="right"
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        paginationPerPage={pageSize}
                        paginationDefaultPage={page}
                        selectableRows={false}
                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                        noDataComponent={loading ? null : 'Kapak bekleyen kayıt bulunamadı'}
                    />
                </Col>
            </Row>

            <Modal isOpen={isCompensationInfoModalOpen} toggle={closeCompensationInfoModal} size="lg">
                <ModalHeader toggle={closeCompensationInfoModal}>Kapak Bilgilerini Güncelle</ModalHeader>
                <ModalBody>
                    {currentCompensationItem?.isSentenced === true ? (
                        <>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Kabul Edilen Tutar</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.acceptedAmount)} onChange={(e) => handleCompensationInfoFieldChange('acceptedAmount', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>İcra Vekalet Ücreti</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.enforcementAttorneyFee)} onChange={(e) => handleCompensationInfoFieldChange('enforcementAttorneyFee', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>İlam Vekalet Ücreti</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.judgmentAttorneyFee)} onChange={(e) => handleCompensationInfoFieldChange('judgmentAttorneyFee', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Artı Masraf Tutarı</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.additionalExpenseAmount)} onChange={(e) => handleCompensationInfoFieldChange('additionalExpenseAmount', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Faiz Tutarı</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.interestAmount)} onChange={(e) => handleCompensationInfoFieldChange('interestAmount', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Kabul Edilen Tutar</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.acceptedAmount)} onChange={(e) => handleCompensationInfoFieldChange('acceptedAmount', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>Faiz Tutarı</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.interestAmount)} onChange={(e) => handleCompensationInfoFieldChange('interestAmount', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                            </Row>
                            <Row>
                                <Col md="6">
                                    <FormGroup>
                                        <Label>İcra Vekalet Ücreti</Label>
                                        <Input type="text" value={displayDecimal(compensationInfoForm.enforcementAttorneyFee)} onChange={(e) => handleCompensationInfoFieldChange('enforcementAttorneyFee', e.target.value)} placeholder="Örn: 1000,50" />
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    )}
                    <hr />
                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label>Açıklama</Label>
                                <Input
                                    type="textarea"
                                    rows="3"
                                    value={compensationInfoForm.comments[0]?.comment || ''}
                                    onChange={(e) => handleCompensationCommentChange(0, 'comment', e.target.value)}
                                    placeholder="Açıklama giriniz..."
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{ color: 'secondary', onClick: closeCompensationInfoModal, disabled: isSubmittingCompensationInfo }}>
                        İptal
                    </Btn>
                    <Btn
                        attrBtn={{
                            color: 'primary',
                            onClick: handleSubmitCompensationInfo,
                            disabled: isSubmittingCompensationInfo || !currentCompensationItem
                        }}
                    >
                        {isSubmittingCompensationInfo ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Kaydediliyor...
                            </>
                        ) : (
                            'Kaydet'
                        )}
                    </Btn>
                </ModalFooter>
            </Modal>
        </Container>
    );
};

export default TodoTabFinalCompensation;
